<template>
  <v-container v-for="page in totalPage" :key="page" fluid class="shipping-result-report">
    <v-row>
      <v-col cols="12" class="text-center report-title">{{ $t('shukkaDetailReport.label.title') }}</v-col>
    </v-row>
    <v-row class="page-block">
      <v-col cols="12" class="text-right">
        {{ $t('shukkaDetailReport.label.page') }} {{ page }} / {{ this.totalPage }}
      </v-col>
    </v-row>

    <v-row class="shipping-info">
      <!-- 出荷情報：左 -->
      <v-col cols="7">
        <v-row>
          <v-col cols="2">{{ $t('shukkaDetailReport.label.originSite') }}</v-col>
          <v-col cols="1">:</v-col>
          <v-col cols="9">
            (<span class="site-code">{{ this.printData.originSiteCode }}</span>)
            {{ this.printData.originSiteName }}</v-col>
        </v-row>
        <v-row>
          <v-col cols="2">{{ $t('shukkaDetailReport.label.tel') }}</v-col>
          <v-col cols="1">:</v-col>
          <v-col cols="9">{{ this.printData.originSiteTel }}</v-col>
        </v-row>
        <v-row>
          <v-col cols="2">{{ $t('shukkaDetailReport.label.destinationSite') }}</v-col>
          <v-col cols="1">:</v-col>
          <v-col cols="9">
            (<span class="site-code">{{ this.printData.destinationSiteCode }}</span>)
            {{ this.printData.destinationSiteName }}</v-col>
        </v-row>
        <v-row>
          <v-col cols="2">{{ $t('shukkaDetailReport.label.tel') }}</v-col>
          <v-col cols="1">:</v-col>
          <v-col cols="9">{{ this.printData.destinationSiteTel }}</v-col>
        </v-row>
        <v-row>
          <v-col cols="2">{{ $t('shukkaDetailReport.label.packingNumber') }}</v-col>
          <v-col cols="1">:</v-col>
          <v-col cols="9">{{ this.getPackingNumberForOutput() }}</v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="pl-8">
            <svg ref="barcode"></svg>
          </v-col>
        </v-row>
      </v-col>

      <!-- 出荷情報：右 -->
      <v-col cols="5">
        <v-row>
          <v-col></v-col>
        </v-row>
        <v-row>
          <v-col cols="4">{{ $t('shukkaDetailReport.label.issueDate') }}</v-col>
          <v-col cols="1">:</v-col>
          <v-col cols="7">{{ issueDate }}</v-col>
        </v-row>
        <v-row>
          <v-col cols="4">{{ $t('shukkaDetailReport.label.staff') }}</v-col>
          <v-col cols="1">:</v-col>
          <v-col cols="7">{{ printUserName }}</v-col>
        </v-row>
        <v-row>
          <v-col></v-col>
        </v-row>
        <v-row>
          <v-col cols="12">{{ this.printData.companyName }}</v-col>
        </v-row>
        <!-- 検印欄 -->
        <v-row>
          <v-col cols="12">
            <v-table density="compact" class="text-no-wrap">
              <thead>
                <tr>
                  <th v-for="n in 3" :key="n" class="text-center">
                    {{ $t('shukkaDetailReport.label.seal') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-for="n in 3" :key="n" class="seal-area"></td>
                </tr>
              </tbody>
            </v-table>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- 一覧 -->
    <v-row>
      <v-col cols="12" class="pt-0">
        <v-table density="compact" class="text-no-wrap detail-list">
          <thead>
            <tr>
              <th rowspan="2" class="text-center">{{ $t('shukkaDetailReport.label.table.no') }}</th>
              <th class="text-center">{{ $t('shukkaDetailReport.label.table.productNumber') }}</th>
              <th class="text-center">{{ $t('shukkaDetailReport.label.table.colorCode') }}</th>
              <th class="text-center">{{ $t('shukkaDetailReport.label.table.sizeCode') }}</th>
              <th class="text-center">{{ $t('shukkaDetailReport.label.table.productCode') }}</th>
              <th rowspan="2" class="text-center"> {{ $t('shukkaDetailReport.label.table.actualQuantity') }} </th>
              <th rowspan="2" class="text-center"> {{ $t('shukkaDetailReport.label.table.retailPrice') }} </th>
              <th rowspan="2" class="text-center">{{ $t('shukkaDetailReport.label.table.price') }}</th>
            </tr>
            <tr>
              <th colspan="3" class="text-center">{{ $t('shukkaDetailReport.label.table.item') }}</th>
              <th class="text-center">{{ $t('shukkaDetailReport.label.table.productName') }}</th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(detail, index) in resultListWithPadding.slice((page - 1) * ROW_PER_PAGE, page * ROW_PER_PAGE)"
              :key="index">
              <tr>
                <td rowspan="2" class="text-center" :style="getColStyle('no')">
                  {{ (page - 1) * ROW_PER_PAGE + index + 1 }}
                </td>
                <td class="text-center small" :style="getColStyle('productNumber')">
                  {{ trimColumnData(detail.productNumber, 'productNumber') }}
                </td>
                <td class="text-center small" :style="getColStyle('colorCode')">
                  {{ trimColumnData(detail.colorCode, 'colorCode') }}
                </td>
                <td class="text-center small" :style="getColStyle('sizeCode')">
                  {{ trimColumnData(detail.sizeCode, 'sizeCode') }}
                </td>
                <td class="text-left small" :style="getColStyle('productCode')">
                  {{ trimColumnData(detail.productCode, 'productCode') }}
                </td>
                <td rowspan="2" class="text-right" :style="getColStyle('actualQuantity')">
                  {{ this.addComma(detail.actualQuantity) }}
                </td>
                <td rowspan="2" class="text-right" :style="getColStyle('retailPrice')">
                  {{ this.addComma(detail.retailPrice) }}
                </td>
                <td rowspan="2" class="text-right" :style="getColStyle('price')">
                  {{ this.addComma(detail.price) }}
                </td>
              </tr>
              <tr>
                <td colspan="3" class="text-left small">
                  {{ detail.item }}
                </td>
                <td class="text-left small" :style="getColStyle('productName')">
                  {{ trimColumnData(detail.productName, 'productName') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </v-col>
    </v-row>

    <!-- フッター -->
    <v-row class="mt-0">
      <!-- 備考・梱包No -->
      <v-col cols="6" class="pt-5 pb-0">
        <v-row class="remark-area ma-0">
          <v-col cols="12" class="pa-0">
            <p>{{ $t('shukkaDetailReport.label.remark') }}</p>
            <p class="small">{{ this.printData.remark }}</p>
          </v-col>
        </v-row>
        <v-row class="mt-4 ma-0 pt-0">
          <v-col cols="3" class="pt-0 pb-0" style="border: 1px solid;">{{ $t('shukkaDetailReport.label.konpoNumber')
            }}</v-col>
          <v-col cols="6" class="pt-0 pb-0" style="border: 1px solid;"></v-col>
          <v-col cols="3" class="pt-0 pb-0"></v-col>
        </v-row>
      </v-col>

      <!-- 集計部 -->
      <v-col cols="6" class="pt-1 pb-0">
        {{ $t('shukkaDetailReport.label.reportRemark1') }}<br />
        {{ $t('shukkaDetailReport.label.reportRemark2') }}<br />
        {{ $t('shukkaDetailReport.label.reportRemark3') }}<br />
        <v-table density="compact" class="text-no-wrap">
          <thead>
            <tr>
              <th style="border: none !important;"></th>
              <th class="text-center">{{ $t('shukkaDetailReport.label.actualQuantity') }}</th>
              <th class="text-center" :style="{minWidth: '10ch'}">{{ $t('shukkaDetailReport.label.retailPrice') }}</th>
              <th class="text-center">{{ $t('shukkaDetailReport.label.price') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th class="text-center">{{ $t('shukkaDetailReport.label.subtotal') }}</th>
              <td class="text-right">{{ this.addComma(this.subtotalList[page - 1].quantity) }}</td>
              <td class="text-right"></td>
              <td class="text-right">{{ this.addComma(this.subtotalList[page - 1].price) }}</td>
            </tr>
            <tr>
              <th class="text-center">{{ $t('shukkaDetailReport.label.total') }}</th>
              <td class="text-right">{{ this.addComma(this.totalQuantity) }}</td>
              <td class="text-right"></td>
              <td class="text-right">{{ this.addComma(this.totalPrice) }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
    </v-row>
    <!-- 印刷途切れ防止用スペーサー -->
    <v-row class="final-page-margin-spacer"></v-row>
  </v-container>
</template>

<script>
import store from "@/store";
import JsBarcode from 'jsbarcode';

/** ページ当たりの表示件数 */
const ROW_PER_PAGE = 20;

export default {
  props: {
    printData: {
      type: Object,
      default: null
    },
  },
  computed: {
    /**  一覧情報を1ページに表示する件数の倍数に調整する */
    resultListWithPadding() {
      const tempArray = (this.printData?.resultList ?? []).slice();
      if (tempArray.length == 0) tempArray.push({});
      if (tempArray.length % ROW_PER_PAGE != 0) {
        const addRecordCount = ROW_PER_PAGE - (tempArray.length % ROW_PER_PAGE);
        for (let i = 0; i < addRecordCount; i++) {
          tempArray.push({});
        }
      }
      return tempArray;
    },
    /** 伝票発行日 */
    issueDate() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0")
      return `${year}年${month}月${day}日`;
    },
    /** 担当 */
    printUserName() {
      return store.getUserName();
    },
  },
  data: () => ({
    /** ページ当たりの表示件数 */
    ROW_PER_PAGE: ROW_PER_PAGE,
    /** 総ページ */
    totalPage: 0,
    /** ページ単位の小計を格納する変数。各要素に{quantity, retailPrice, price}の集計値を持つ */
    subtotalList: [],
    /** 総合計数量 */
    totalQuantity: 0,
    /** 総合計金額 */
    totalPrice: 0,
    /** 総合計上代 */
    totalRetailPrice: 0,

    /**
     * 最大桁数情報
     * 
     * len: trim処理または表示幅の調整に使用する想定桁数 \
     * unit: 表示幅調整の際に使用する単位。基本半角文字想定なのでchとしている。
     * 
     * 対向システムの最大サイズを前提としている \
     * 数値系は最大サイズを下回るが、trim処理をしないので列幅の調整に使用している
     * 
     * 文言系はこの桁数を上回るデータが来た場合、trim処理をする \
     *   基本的に対向の最大サイズを担保しているのでtrim処理は発生しない \
     *   productNameは最大桁が60なので発生しうる
     */
    charMaxLengths: {
      no: { len: 2, unit: 'ch' },

      // 数値系。カンマの文字数も含まれる
      actualQuantity: { len: 7, unit: 'ch' },
      retailPrice: { len: 10, unit: 'ch' },
      price: { len: 15, unit: 'ch' },
      // 文言系
      productNumber: { len: 16, unit: 'ch' },
      colorCode: { len: 6, unit: 'ch' },
      sizeCode: { len: 6, unit: 'ch' },
      productCode: { len: 30, unit: 'ch' },
      productName: { len: 30, unit: 'ch' },
    }
  }),

  methods: {
    /**
     * 列に適用するstyleを生成する
     * @param colName 列名
     */
    getColStyle(colName) {

      const setting = this.charMaxLengths[colName];
      const width = setting.len + setting.unit;

      if (['productName', 'productCode'].includes(colName)) {
        return { width: width };
      }

      return { width: width, maxWidth: width };
    },
    /**
     * 最大文字数を超えた部分は省略する
     * @param v 値
     * @param type 列種別 
     */
    trimColumnData(v, type) {
      if (v == null) return "";
      const s = "" + v;
      const maxLength = this.charMaxLengths[type].len;
      if (s.length <= maxLength) return v;
      return s.substring(0, maxLength - 3) + "...";
    },
    /**
     * 数値にカンマを付与する
     * @param money
     */
    addComma(money) {
      if (!Number.isFinite(money)) return money;
      return money.toLocaleString("ja-JP");
    },
    /** 表示用伝票番号生成 */
    getPackingNumberForOutput() {
      if (this.printData == null) return '';
      return `${this.printData.originSiteCode}-40-${this.printData.packingNumber}`;
    },
    /** バーコード生成用伝票番号生成 */
    getPackingNumberForPrint() {
      if (this.printData == null) return '';
      return `${this.printData.originSiteCode}40${this.printData.packingNumber}`;
    },
    /** 印刷前の集計処理 */
    print() {
      this.totalPage = Math.ceil(this.printData.resultList.length / ROW_PER_PAGE);
      if (this.totalPage == 0) this.totalPage = 1;

      this.totalQuantity = 0;
      this.totalPrice = 0;
      this.totalRetailPrice = 0;
      this.subtotalList = [...Array(this.totalPage)].map(_ => ({
        price: 0,
        quantity: 0,
        retailPrice: 0
      }));

      for (let i = 0; i < this.printData.resultList.length; i++) {
        const detail = this.printData.resultList[i];
        const quantity = Number.isFinite(detail.actualQuantity) ? detail.actualQuantity : 0;
        const price = Number.isFinite(detail.price) ? detail.price : 0;
        const retailPrice = Number.isFinite(detail.price) ? detail.retailPrice : 0;

        const idx = Math.floor(i / ROW_PER_PAGE);
        this.subtotalList[idx].quantity += quantity;
        this.subtotalList[idx].price += price;
        this.subtotalList[idx].retailPrice += retailPrice;

        this.totalQuantity += quantity;
        this.totalPrice += price;
        this.totalRetailPrice += retailPrice;
      }
    },
  },
  watch: {
    printData: 'print'
  },
  updated() {
    if (this.totalPage == 0) return;
    // バーコード生成
    const packingNumber = this.getPackingNumberForPrint();
    JsBarcode(this.$refs.barcode, packingNumber, {
      format: "CODE39",
      flat: true,
      height: 40,
      width: 1.3,
      fontSize: 10,
      text: packingNumber
    });
  }
};
</script>

<style scoped>
.shipping-result-report {
  display: none;

  * {
    font-size: 11px;
  }

  table {
    border-collapse: collapse;
  }

  table,
  th,
  td {
    border: 1px solid !important;
  }

  tr,
  th,
  td {
    height: 1rem !important;
    padding: 0 2px !important;
  }

  .report-title {
    font-size: 20px;
    padding: 0 !important;
  }

  .page-block {
    margin-top: -1rem;
  }

  .small {
    font-size: 9px !important;
  }

  .shipping-info {
    padding-bottom: 0rem;
    margin-top: 0;
    margin-bottom: 0rem;

    .site-code {
      padding: 0 1rem;
    }

    .v-col {
      padding-top: 0;
      padding-bottom: 0;
      min-height: 1rem;
    }

    .seal-area {
      height: 50px !important;
    }
  }

  .remark-area {
    border: 1px solid;
    min-height: 3rem;
  }

  .final-page-margin-spacer {
    height: 50px;
  }
}

@media print {
  .shipping-result-report {
    display: block;
    break-after: page;
  }
}
</style>
